import React, {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

interface InputProps {
    label: ReactNode;
    description?: string;
    id: string;
    isShow? : boolean;
    type?: string;
    required?: boolean;
    value?: string | number | readonly string[] | undefined;
    onchange: (e: any) => void;
    attributes?: any;
    className?: string;
    error?: boolean;
    unit?: string;
    size?: 'default' | 'lg';
}

export default function Input({
                                  label,
                                  description,
                                  id,
                                  isShow = false,
                                  type = 'text',
                                  value,
                                  onchange,
                                  attributes,
                                  className,
                                  unit,
                                  required = false,
                                  error = false,
                                  size= 'default'
                              }: InputProps) {

    const {t} = useTranslation();

    const sizeClasses = size === 'lg'
        ? 'py-3 px-4 text-base sm:text-3xl'
        : 'py-2 px-2 sm:text-sm';

    // Styles spécifiques pour les inputs time/date (problème de visibilité sur iOS Safari)
    const isDateTimeInput = type === 'time' || type === 'date' || type === 'datetime-local';
    const dateTimeStyles = isDateTimeInput
        ? '[color-scheme:light] [-webkit-text-fill-color:rgb(17,24,39)] [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-datetime-edit]:text-gray-900 [-webkit-appearance:none] [appearance:none] !ring-1 !ring-inset !ring-gray-300 !rounded-md !min-w-[120px]'
        : '';

    // Placeholder par défaut pour les inputs time/date, ou celui passé via attributes
    const getPlaceholder = () => {
        if (type === 'time') return '--:--';
        if (type === 'date') return t('survey:global.placeholders.date', 'jj/mm/aaaa');
        if (type === 'datetime-local') return t('survey:global.placeholders.datetime', 'jj/mm/aaaa --:--');
        return attributes?.placeholder ?? undefined;
    };

    return (
        <div className="relative group">
            <label
                htmlFor={id}
                title={typeof label === 'string' ? label : undefined}
                className={`z-10 absolute -top-2 left-2 inline-block rounded px-1 text-xs font-light bg-white
                max-w-[calc(100%-0.75rem)] truncate whitespace-nowrap pointer-events-none
                ${error ? 'text-red-500' : 'text-gray-900'}
                ${isShow ? 'hidden group-hover:block group-focus:block focus:block' : '' }`}
            >
                {label}
            </label>
            <div className="flex items-center space-x-2">
                <input id={id}
                       {...attributes}
                       value={value}
                       onChange={onchange}
                       type={type}
                       required={required}
                       placeholder={getPlaceholder()}
                       className={`block w-full rounded-md border-0 py-2 px-2 text-gray-900 bg-white
                    placeholder:text-gray-400 sm:leading-6
                    ${error ? 'ring-red-500' : 'ring-gray-300'}
                    ${isShow ? 'ring-0 group-hover:ring-1 group-hover:ring-inset group-hover:shadow-sm group-focus:ring-inset' +
                           'group-focus:ring-indigo-600 group-focus:focus:ring-2 '
                           : 'ring-1 ring-inset shadow-sm focus:ring-2 focus:ring-inset focus:ring-indigo-600'}
                    ${sizeClasses}
                    ${dateTimeStyles}
                    ${className}`}
                />
                {unit ? <span className="text-gray-500">{unit}</span> : null}
            </div>
            {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
    );

}

